import React from 'react';

/**
 * Custom SVG illustrations for Kanban column empty states and headers.
 * Hand-crafted, minimal line art style with status-contextual colors.
 */

// ─── EMPTY STATE ILLUSTRATIONS ─────────────────────────────────────────

export const AnalysisEmptyIllustration = ({ color = '#8b5cf6', size = 80 }) => (
    <svg width={size} height={size} viewBox="0 0 80 80" fill="none" xmlns="http://www.w3.org/2000/svg">
        {/* Order ticket / receipt */}
        <rect x="20" y="12" width="40" height="56" rx="4" fill={`${color}08`} stroke={color} strokeWidth="1.5" strokeDasharray="0" />
        {/* Torn bottom edge */}
        <path d="M20 62 L24 58 L28 62 L32 58 L36 62 L40 58 L44 62 L48 58 L52 62 L56 58 L60 62 L60 68 L20 68 Z" fill={`${color}06`} stroke={color} strokeWidth="1.5" strokeLinejoin="round" />
        {/* Content lines on receipt */}
        <line x1="28" y1="24" x2="52" y2="24" stroke={color} strokeWidth="1.5" strokeLinecap="round" opacity="0.6" />
        <line x1="28" y1="31" x2="46" y2="31" stroke={color} strokeWidth="1.5" strokeLinecap="round" opacity="0.4" />
        <line x1="28" y1="38" x2="50" y2="38" stroke={color} strokeWidth="1.5" strokeLinecap="round" opacity="0.4" />
        <line x1="28" y1="45" x2="42" y2="45" stroke={color} strokeWidth="1.5" strokeLinecap="round" opacity="0.3" />
        {/* Small clock overlay */}
        <circle cx="54" cy="20" r="10" fill="white" stroke={color} strokeWidth="1.5" />
        <circle cx="54" cy="20" r="7" fill={`${color}10`} stroke={color} strokeWidth="1" />
        <line x1="54" y1="16" x2="54" y2="20" stroke={color} strokeWidth="1.5" strokeLinecap="round" />
        <line x1="54" y1="20" x2="57" y2="22" stroke={color} strokeWidth="1.5" strokeLinecap="round" />
    </svg>
);

export const ProductionEmptyIllustration = ({ color = '#f59e0b', size = 80 }) => (
    <svg width={size} height={size} viewBox="0 0 80 80" fill="none" xmlns="http://www.w3.org/2000/svg">
        {/* Pan / skillet */}
        <ellipse cx="36" cy="52" rx="22" ry="8" fill={`${color}10`} stroke={color} strokeWidth="1.5" />
        <path d="M14 52 C14 42, 58 42, 58 52" fill={`${color}08`} stroke={color} strokeWidth="1.5" strokeLinecap="round" />
        {/* Handle */}
        <line x1="58" y1="48" x2="72" y2="42" stroke={color} strokeWidth="2.5" strokeLinecap="round" />
        <circle cx="72" cy="42" r="2" fill={color} opacity="0.4" />
        {/* Steam lines - animated feel */}
        <path d="M28 38 C28 34, 32 34, 32 30" stroke={color} strokeWidth="1.5" strokeLinecap="round" opacity="0.5" />
        <path d="M36 36 C36 31, 40 31, 40 26" stroke={color} strokeWidth="1.5" strokeLinecap="round" opacity="0.4" />
        <path d="M44 38 C44 34, 48 34, 48 30" stroke={color} strokeWidth="1.5" strokeLinecap="round" opacity="0.3" />
        {/* Small flame under */}
        <path d="M30 60 C30 56, 34 55, 34 58 C34 55, 38 56, 38 60" stroke={color} strokeWidth="1" strokeLinecap="round" opacity="0.3" fill={`${color}08`} />
        <path d="M38 60 C38 57, 41 56, 41 58.5 C41 56, 44 57, 44 60" stroke={color} strokeWidth="1" strokeLinecap="round" opacity="0.25" fill={`${color}06`} />
    </svg>
);

export const ReadyEmptyIllustration = ({ color = '#10b981', size = 80 }) => (
    <svg width={size} height={size} viewBox="0 0 80 80" fill="none" xmlns="http://www.w3.org/2000/svg">
        {/* Paper bag */}
        <path d="M18 30 L18 64 C18 66.2 19.8 68 22 68 L58 68 C60.2 68 62 66.2 62 64 L62 30" fill={`${color}06`} stroke={color} strokeWidth="1.5" strokeLinejoin="round" />
        {/* Bag top fold */}
        <path d="M16 30 L64 30 L62 24 L18 24 Z" fill={`${color}10`} stroke={color} strokeWidth="1.5" strokeLinejoin="round" />
        {/* Bag handles */}
        <path d="M30 24 C30 18, 30 14, 36 14" stroke={color} strokeWidth="1.5" strokeLinecap="round" fill="none" opacity="0.6" />
        <path d="M50 24 C50 18, 50 14, 44 14" stroke={color} strokeWidth="1.5" strokeLinecap="round" fill="none" opacity="0.6" />
        {/* Checkmark circle */}
        <circle cx="40" cy="48" r="12" fill={`${color}15`} stroke={color} strokeWidth="1.5" />
        {/* Checkmark */}
        <polyline points="34,48 38,52 46,44" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

export const CompletedEmptyIllustration = ({ color = '#06b6d4', size = 80 }) => (
    <svg width={size} height={size} viewBox="0 0 80 80" fill="none" xmlns="http://www.w3.org/2000/svg">
        {/* Delivery bag base */}
        <path d="M22 34 L22 58 C22 60.2 23.8 62 26 62 L54 62 C56.2 62 58 60.2 58 58 L58 34" fill={`${color}06`} stroke={color} strokeWidth="1.5" strokeLinejoin="round" />
        <rect x="20" y="30" width="40" height="6" rx="2" fill={`${color}10`} stroke={color} strokeWidth="1.5" />
        {/* Small star/sparkle - top left */}
        <path d="M16 22 L17.5 18 L19 22 L23 23.5 L19 25 L17.5 29 L16 25 L12 23.5 Z" fill={color} opacity="0.3" />
        {/* Small star/sparkle - top right */}
        <path d="M60 18 L61 15 L62 18 L65 19 L62 20 L61 23 L60 20 L57 19 Z" fill={color} opacity="0.25" />
        {/* Tiny dot sparkles */}
        <circle cx="26" cy="16" r="1.5" fill={color} opacity="0.2" />
        <circle cx="56" cy="26" r="1" fill={color} opacity="0.2" />
        <circle cx="68" cy="14" r="1.5" fill={color} opacity="0.15" />
        {/* Big checkmark */}
        <circle cx="40" cy="46" r="10" fill={`${color}15`} stroke={color} strokeWidth="1.5" />
        <polyline points="35,46 38.5,50 45,42" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        {/* Smile under the bag */}
        <path d="M34 56 C36 58, 44 58, 46 56" stroke={color} strokeWidth="1.2" strokeLinecap="round" fill="none" opacity="0.35" />
    </svg>
);


// ─── HEADER ICONS (small, 20x20) ───────────────────────────────────────

export const AnalysisHeaderIcon = ({ color = '#8b5cf6', size = 18 }) => (
    <svg width={size} height={size} viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
        {/* Clipboard */}
        <rect x="3" y="3" width="12" height="14" rx="2" stroke={color} strokeWidth="1.4" fill="none" />
        <rect x="6" y="1" width="6" height="3" rx="1" fill={color} opacity="0.25" stroke={color} strokeWidth="1" />
        {/* Lines */}
        <line x1="6" y1="8" x2="12" y2="8" stroke={color} strokeWidth="1.2" strokeLinecap="round" opacity="0.6" />
        <line x1="6" y1="11" x2="10" y2="11" stroke={color} strokeWidth="1.2" strokeLinecap="round" opacity="0.4" />
    </svg>
);

export const ProductionHeaderIcon = ({ color = '#f59e0b', size = 18 }) => (
    <svg width={size} height={size} viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
        {/* Timer / stopwatch */}
        <circle cx="9" cy="10" r="6.5" stroke={color} strokeWidth="1.4" fill="none" />
        <line x1="9" y1="7" x2="9" y2="10" stroke={color} strokeWidth="1.4" strokeLinecap="round" />
        <line x1="9" y1="10" x2="11.5" y2="11.5" stroke={color} strokeWidth="1.4" strokeLinecap="round" />
        {/* Top button */}
        <line x1="8" y1="2" x2="10" y2="2" stroke={color} strokeWidth="1.4" strokeLinecap="round" />
        <line x1="9" y1="2" x2="9" y2="3.5" stroke={color} strokeWidth="1.2" strokeLinecap="round" />
    </svg>
);

export const ReadyHeaderIcon = ({ color = '#10b981', size = 18 }) => (
    <svg width={size} height={size} viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
        {/* Circle check */}
        <circle cx="9" cy="9" r="7" stroke={color} strokeWidth="1.4" fill="none" />
        <polyline points="6,9 8,11.5 12,6.5" fill="none" stroke={color} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

export const CompletedHeaderIcon = ({ color = '#06b6d4', size = 18 }) => (
    <svg width={size} height={size} viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
        {/* Flag / finish */}
        <line x1="4" y1="3" x2="4" y2="16" stroke={color} strokeWidth="1.4" strokeLinecap="round" />
        <path d="M4 3 C7 1, 11 5, 14 3 L14 10 C11 12, 7 8, 4 10" fill={`${color}20`} stroke={color} strokeWidth="1.3" strokeLinejoin="round" />
    </svg>
);
